import { Controller } from "@hotwired/stimulus"
import PlayableList from "../player/playable_list"

const sharedState = {
  isPlaying: false,
  isHD: false,
  isFullscreen: false,
  players: []
}

export default class extends Controller {
  static targets = [
    "playButton",
    "pauseButton",
    "previousButton",
    "nextButton",
    "player",
    "progress",
    "progressFull",
    "title",
    "artist",
    "composerName",
    "performerName",
    "trackCover",
    "artistImage",
    "controls",
    "currentTime",
    "duration",
    "hd",
    "fullsize",
    "smallsize",
    "volume",
    "volumeMenu",
    "expandDialog",
    "expandToggle",
    "expandClose",
    "titleLink",
    "artistLink",
    "composerLink",
    "performerLink",
    "playlistName",
    "playlistIndex",
    "playlistLength",
    "playlistLink",
    "videoHeader",
    "favouriteLink",
  ]
  static classes = ["show", "hidden", "active"]
  static stickyVideo = false
  static playerMovedToPlaceholder = false

  connect() {
    if (!sharedState.players.includes(this)) {
      sharedState.players.push(this)
      sharedState.playableList = new PlayableList()
    }

    this.playableList = sharedState.playableList
    this.placeholder = document.getElementById("placeholder")
    this.placeholderSpace = document.getElementById("placeholder-space")
    this.header = document.querySelector('[data-type="navtop"]')
    this.playlistControls = document.querySelector("#playlist-controls")
    this.previousWidth = window.innerWidth
    const self = this

    document.addEventListener("turbo:before-render", function (e) {
      self.resetVideoPosition()
    })

    this.lastExpandTrigger = null

    document.addEventListener("turbo:before-visit", () => {
      if (!this.hasExpandDialogTarget) return

      this.expandDialogTarget.classList.remove("!translate-y-0")
      this.expandDialogTarget.classList.remove("sheet")
      this.expandDialogTarget.style.marginBottom = ""
      this.expandDialogTarget.setAttribute("aria-hidden", "true")
      this.expandToggleTargets.forEach((item) => {
        item.style.transform = ""
        item.setAttribute("aria-expanded", "false")
      })
    })

    if (this.hasExpandDialogTarget) {
      const isOpen = this.expandDialogTarget.classList.contains("!translate-y-0")
      this.expandDialogTarget.setAttribute("aria-hidden", String(!isOpen))
      this.expandToggleTargets.forEach((item) => {
        item.setAttribute("aria-expanded", String(isOpen))
      })
    }

    if (document.querySelector(".track-page") && window.location.hash == "#play") {
      document.querySelector("#placeholder").click()
    }

    if (this.hasHdTarget) {
      this.hdTarget.setAttribute('aria-pressed', String(sharedState.isHD))
    }

    // handle clicks on the embed like playing/pausing/removing show-more
    if (this.isEmbed()) {
      document.addEventListener("mouseenter", this.showControls.bind(this))
      document.addEventListener("mouseleave", this.hideControls.bind(this))
      // handle clicks on whole embed player:
      this.element.addEventListener("click", (e) => {     
        // if the "three-dot-view" is being shown AND the click is not on the trigger -> hide the "three-dot-view"
        if (this.element.classList.contains("more-shown") && !e.target.closest(".more-trigger")) {
          this.element.classList.remove("more-shown")
          if (!e.target.closest('[data-player-target="playButton"], [data-player-target="pauseButton"]')) {
            return // do not toggle the player if the click was outside of the controls
          }
        }
        // if the click is anywhere BUT on the controls, the video header or the "three-dot-view" -> resume/stop the player
        if (e.target.closest('[data-player-target="controls"], [data-player-target="videoHeader"], .more-container') || e.target.tagName.toLowerCase() === "a") return
        if (e.target.closest(".video-parent")) {
          sharedState.isPlaying ? self.pauseButtonTarget.click() : self.playButtonTarget.click()
        }
      })
    }

    this.restoreCurrentTrackPlayer(this, sharedState)
  }

  /*
   * API
   */

  play(event) {
    this.showPlayer()
    if (event.detail.name || event.target.getAttribute("id") == "placeholder") {
      this.positionVideo()
    } else {
      this.resetVideoPosition()
    }
    this.playableList.playNow(event.detail)
    this.switchTo(this.playableList.currentTrack)
    if (!this.playableList.current.hasNext() && !this.playableList.current.hasPrevious()) {
      this.hidePreviousNextButtons()
    } else {
      this.showPreviousNextButtons()
    }
    this.showControls()
  }

  playOrResume(event) {
    if (this.playableList.current && event.detail.path == this.playableList.current.path) {
      this.resume()
    } else {
      this.play(event)
    }
  }

  positionVideo() {
    const placeholder = document.getElementById("placeholder")
    if (placeholder) {
      const rect = placeholder.getBoundingClientRect()
      Object.assign(this.playerTarget.parentElement.style, {
        display: 'block',
        position: this.stickyVideo ? 'fixed' : 'absolute',
        left: (window.scrollX + rect.left) + 'px',
        top: this.stickyVideo ? (this.header.getBoundingClientRect().height + (window.innerWidth >= 1024 ? 20 : 0) + 'px') : (window.scrollY + rect.top + 'px'),
        height: rect.height + 'px',
        width: rect.width + 'px',
        zIndex: 60
      })
      placeholder.querySelector('[data-playable-target="placeholder-image"]').classList.add("opacity-0")
      this.playerMovedToPlaceholder = true
    }
  }

  resetVideoPosition() {
    const placeholder = document.getElementById("placeholder")
    if (placeholder) {
      this.playerTarget.parentElement.style = ""
      placeholder.querySelector('[data-playable-target="placeholder-image"]').classList.remove("opacity-0")
      this.playerMovedToPlaceholder = false
    }
  }

  scroll(event, forceResetFixedElems) {
    // Check if necessary elements exist before proceeding
    if (!this.placeholderSpace || !this.header || !this.placeholder) return

    const isMobile = window.innerWidth <= 768
    // only stick the player if it's a playlist page
    if (document.querySelectorAll('[data-playlist-target="play"]').length == 0 || document.querySelectorAll("#trackPlay").length == 1) return

    // calculate the position where the player changes to "fixed", basically when it reaches the nav
    let targetPosition = this.placeholderSpace.getBoundingClientRect().top - this.header.getBoundingClientRect().height - 20
    if (targetPosition < 0 && !this.placeholder.classList.contains("fixed")) {
      // Store the original dimensions of the placeholder
      const originalHeight = this.placeholder.getBoundingClientRect().height
      const originalWidth = this.placeholder.getBoundingClientRect().width

      // position placeholder-space element in the position of the placeholder to prevent changes of the doc height
      if (window.innerWidth >= 1024) {
        Object.assign(this.header.style, {
          height: this.header.getBoundingClientRect().height + 20 + "px",
          paddingBottom: "20px"
        })
      }
      Object.assign(this.placeholderSpace.style, {
        height: originalHeight + "px",
        width: originalWidth + "px"
      })
      this.placeholder.classList.add("fixed")
      // position placeholder element to fixed at the top of the page below the nav
      Object.assign(this.placeholder.style, {
        position: "fixed",
        width: originalWidth + "px",
        top: this.header.getBoundingClientRect().height + (window.innerWidth >= 1024 ? 20 : 0) + "px",  // on desktop, we need 20px more spacing to the top
      })
      // position controls below sticky video, if mobile
      if (isMobile) {
        Object.assign(this.playlistControls.style, {
          top: this.header.getBoundingClientRect().height + (window.innerWidth >= 1024 ? 20 : 0) + this.placeholder.getBoundingClientRect().height + "px",
        })
        this.playlistControls.classList.add("sticky")
      }
      this.stickyVideo = true
      if (this.playerMovedToPlaceholder) this.positionVideo() // if the player was started, also reposition the player
    } else if ((targetPosition >= 0 && this.placeholder.classList.contains("fixed")) || forceResetFixedElems) {
      // reset everything to it's original styles
      this.placeholder.classList.remove("fixed")
      this.placeholderSpace.style.height = ""
      this.placeholderSpace.style.width = ""
      this.placeholder.style.height = ""
      this.placeholder.style.width = ""
      this.placeholder.style.position = ""
      this.header.style.height = ""
      this.header.style.paddingBottom = ""
      this.stickyVideo = false
      this.playlistControls.classList.remove("sticky")
      if (forceResetFixedElems) this.scroll()
      if (this.playerMovedToPlaceholder) this.positionVideo()
    }
  }

  repositionVideo() {
    const currentWidth = window.innerWidth
    if (currentWidth == this.previousWidth) return
    if (this.stickyVideo) {
      this.scroll(null, true)
    }
    if (this.playerMovedToPlaceholder) {
      this.positionVideo()
    }
    this.previousWidth = currentWidth
  }

  resume() {
    this.playerTarget.play()
  }

  pause() {
    this.playerTarget.pause()
  }

  next() {
    if (!this.playableList.current) return
    let old = this.playableList.currentTrack
    this.playableList.skipForward()
    if (old !== this.playableList.currentTrack) {
      this.switchTo(this.playableList.currentTrack)
      this.dispatch("updateIndex", { detail: this.playableList.current })
    }
  }

  previous() {
    if (!this.playableList.current) return
    let old = this.playableList.currentTrack
    this.playableList.skipBackward()
    if (old !== this.playableList.currentTrack) {
      this.switchTo(this.playableList.currentTrack)
      this.dispatch("updateIndex", { detail: this.playableList.current })
    }
  }

  skipToTrack(event) {
    let indexToPlay = event.detail
    this.playableList.skipToTrack(indexToPlay)
    this.switchTo(this.playableList.currentTrack)
  }

  switchTo(track) {
    if (sharedState.isHD && track.hd_url) {
      this.playSource(track.hd_url)
    } else {
      this.playSource(track.url)
    }
    this.updateInfo(track)
    this.dispatch("play", { detail: track.path })
    if (this.playableList.current) this.dispatch("updateIndex", { detail: this.playableList.current })
    this.setPoster(track)
  }

  playSource(source) {
    this.playerTarget.src = source
    this.playerTarget.load()
  }

  handleError(error) {
    this.playerTarget.pause()
  }

  toggleHd(event) {
    event.preventDefault()
    let currentTime = this.currentTime
    sharedState.isHD = !sharedState.isHD
    if (sharedState.isHD) {
      this.playSource(this.playableList.currentTrack.hd_url)
      this.hdTarget.classList.add(this.activeClass)
    } else {
      this.playSource(this.playableList.currentTrack.url)
      this.hdTarget.classList.remove(this.activeClass)
    }
    this.hdTarget.setAttribute('aria-pressed', String(sharedState.isHD))
    this.playerTarget.currentTime = currentTime
  }

  toggleFullsize(event) {
    event.preventDefault()
    let target = this.playerTarget.parentElement

    if (!this.isFullscreenSupported(target)) {
      target = this.playerTarget // fallback using the actual video with native controls
    }

    if (!sharedState.isFullscreen) {
      if (target.requestFullscreen) {
        target.requestFullscreen()
      } else if (target.mozRequestFullScreen) {
        target.mozRequestFullScreen()
      } else if (target.webkitRequestFullscreen) {
        target.webkitRequestFullscreen()
      } else if (target.webkitEnterFullscreen) {
        target.webkitEnterFullscreen()
      } else if (target.msRequestFullscreen) {
        target.msRequestFullscreen()
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen()
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen()
      }
    }
  }

  fullscreenChange() {
    if (!document.fullscreenElement && !document.webkitFullscreenElement &&
      !document.mozFullScreenElement && !document.msFullscreenElement) {
      // fullscreen closed
      this.fullsizeTarget.classList.remove(this.hiddenClass)
      this.smallsizeTarget.classList.add(this.hiddenClass)
      sharedState.isFullscreen = false
    } else {
      // fullscreen opened
      this.fullsizeTarget.classList.add(this.hiddenClass)
      this.smallsizeTarget.classList.remove(this.hiddenClass)
      sharedState.isFullscreen = true
    }
  }

  isFullscreenSupported(target) {
    return target.requestFullscreen ||
      target.mozRequestFullScreen ||
      target.webkitRequestFullscreen ||
      target.msRequestFullscreen
  }
  toggleVolume(event) {
    event.preventDefault()
    const isExpanded = !this.volumeTarget.classList.contains(this.activeClass)
    this.volumeTarget.classList.toggle(this.activeClass, isExpanded)
    this.volumeTarget.setAttribute('aria-expanded', String(isExpanded))
    if (this.hasVolumeMenuTarget) {
      this.volumeMenuTarget.setAttribute('aria-hidden', String(!isExpanded))
    }
  }

  toggleExpand(event) {
    if (event?.currentTarget instanceof HTMLElement && event.currentTarget.dataset.playerTarget !== "expandClose") {
      this.lastExpandTrigger = event.currentTarget
    }

    const isOpen = this.expandDialogTarget.classList.toggle("!translate-y-0")
    this.expandDialogTarget.classList.toggle("sheet", isOpen)

    this.expandToggleTargets.forEach((item) => {
      item.style.transform = isOpen ? "rotate(180deg)" : ""
      item.setAttribute("aria-expanded", String(isOpen))
    })

    this.expandDialogTarget.setAttribute("aria-hidden", String(!isOpen))

    if (isOpen) {
      const impersonationBanner = document.querySelector("#impersonation_banner")
      if (impersonationBanner) {
        this.expandDialogTarget.style.marginBottom = impersonationBanner.getBoundingClientRect().height + "px"
      }

      requestAnimationFrame(() => {
        this.expandCloseTarget?.focus()
      })
    } else {
      this.expandDialogTarget.style.marginBottom = ""
      if (this.lastExpandTrigger?.focus) {
        this.lastExpandTrigger.focus()
      }
    }
  }

  changeVolume(event) {
    const value = event.target.value
    this.playerTarget.volume = value / 100
    event.target.setAttribute('aria-valuenow', value)
  }

  updateExpandToggleLabel(toggle, isOpen) {
    if (!toggle) return
    const expandedLabel = toggle.dataset.playerExpandedLabel
    const collapsedLabel = toggle.dataset.playerCollapsedLabel
    if (expandedLabel && collapsedLabel) {
      toggle.setAttribute('aria-label', isOpen ? expandedLabel : collapsedLabel)
    }
  }

  // On some browsers and devices, programmatic playback is only allowed once
  // the media element has played at least one source in response to user input.
  // We play a silent track to achieve that.
  unlockPlayback() {
    if (sharedState.isPlaying) return
    const silentAudio = "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA"
    this.playerTarget.muted = true
    this.playerTarget.src = silentAudio
    this.playerTarget.play()
      .catch((error) => this.handleError(error))
      .then(() => {
        this.playerTarget.pause()
        this.playerTarget.muted = false
      })
  }

  loaded() {
    this.playerTarget.play().catch((error) => this.handleError(error))
  }

  /*
   * Events
   *
   * Named after HTMLMediaElement events
   * https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement#events
   */

  playing(event) {
    console.log("event: playing")
    this.hidePlayButton()
    this.showPauseButton()
    sharedState.isPlaying = true
    this.broadcastState()
    this.element.classList.add("started")
    this.element.classList.add("playing")
  }

  paused(event) {
    console.log("event: paused")
    this.hidePauseButton()
    this.showPlayButton()
    sharedState.isPlaying = false
    this.broadcastState()
    this.showControls()
    this.element.classList.remove("playing")
  }

  ended(event) {
    console.log("event: ended")
    this.hidePauseButton()
    this.showPlayButton()
    this.next()
    this.element.classList.remove("playing")
  }

  // https://developer.mozilla.org/en-US/docs/Web/API/MediaError
  error(event) {
    handleError(event.error)
  }

  timeupdate(event) {
    this.updateProgress()
  }

  durationchange(event) {
    this.updateProgress()

    this.durationTargets.forEach((item) => {
      item.innerText = this.formatSecondsToMinutes(this.duration)
    })
  }

  /*
   * User interface
   */

  showPlayer() {
    this.element.classList.remove(this.hiddenClass)
    this.element.classList.add(this.showClass)
    // if we have an impersionation banner, position the player above
    if (document.querySelector("#impersonation_banner")) {
      this.element.querySelector(".sheet").style.bottom = document.querySelector("#impersonation_banner").getBoundingClientRect().height + "px"
    }
  }

  hidePlaceholder() {
    const placeholder = document.getElementById("placeholder")
    if (placeholder) {
      placeholder.classList.add(this.hiddenClass)
    }
  }

  hidePlayButton() {
    this.playButtonTargets.forEach((item) => {
      item.classList.add(this.hiddenClass)
      item.classList.remove(this.showClass)
    })
    this.dispatch("hidePlayButton")
  }

  showPreviousNextButtons() {
    this.previousButtonTargets.forEach((item) => {
      item.classList.add(this.showClass)
      item.classList.remove(this.hiddenClass)
    })
    this.nextButtonTargets.forEach((item) => {
      item.classList.add(this.showClass)
      item.classList.remove(this.hiddenClass)
    })
  }

  hidePreviousNextButtons() {
    this.previousButtonTargets.forEach((item) => {
      item.classList.add(this.hiddenClass)
      item.classList.remove(this.showClass)
    })
    this.nextButtonTargets.forEach((item) => {
      item.classList.add(this.hiddenClass)
      item.classList.remove(this.showClass)
    })
  }

  showPlayButton() {
    this.playButtonTargets.forEach((item) => {
      item.classList.remove(this.hiddenClass)
      item.classList.add(this.showClass)
    })
    this.dispatch("showPlayButton")
  }

  hidePauseButton() {
    this.pauseButtonTargets.forEach((item) => {
      item.classList.remove(this.showClass)
      item.classList.add(this.hiddenClass)
    })
    this.dispatch("hidePauseButton")
  }

  showPauseButton() {
    this.pauseButtonTargets.forEach((item) => {
      item.classList.remove(this.hiddenClass)
      item.classList.add(this.showClass)
    })
    this.dispatch("showPauseButton")
  }

  seeked(event) {
    let x = event.clientX - event.target.getBoundingClientRect().left
    let width = event.target.getBoundingClientRect().width
    let percent = (x / width) * 100
    let newTime = this.duration / 100 * percent
    this.playerTarget.currentTime = newTime
    this.updateProgress()
  }

  updateProgress() {
    const percentage = this.currentTime * 100 / this.duration

    this.progressTargets.forEach((item) => {
      item.style = `width: ${percentage}%`

      const currentSeconds = Math.round(this.currentTime)
      item.setAttribute("aria-valuenow", currentSeconds.toString())
      item.setAttribute("aria-valuetext", `${this.formatSecondsToMinutes(this.currentTime)} of ${this.formatSecondsToMinutes(this.duration)}`)
    })

    this.currentTimeTargets.forEach((item) => {
      item.innerText = this.formatSecondsToMinutes(this.currentTime)
    })
  }

  updateInfo(track) {
    if (this.hasTitleTarget) {
      this.titleTargets.forEach((item) => {
        item.innerText = track.title
      })
    }
    if (this.hasArtistTarget) {
      this.artistTargets.forEach((item) => {
        if (track.performerships) {
          item.innerText = track.artist + ' (ft. ' + track.performerships + ')'
        } else {
          item.innerText = track.artist
        }
        
      })
    }
    if (this.hasComposerNameTarget) {
      this.composerNameTargets.forEach((item) => {
        item.innerText = track.composer_name
      })
    }
    if (this.hasPerformerNameTarget) {
      this.performerNameTargets.forEach((item) => {
        item.innerText = track.performer_name
      })
    }
    if (this.hasTrackCoverTarget) {
      this.trackCoverTargets.forEach((item) => {
        item.src = track.picture_url_xlarge
      })
    }
    if (this.hasArtistImageTarget) {
      this.artistImageTargets.forEach((item) => {
        item.src = track.user_picture_url
      })
    }
    if (this.hasTitleLinkTarget) {
      this.titleLinkTargets.forEach((item) => {
        item.href = track.path
      })
    }
    if (this.hasArtistLinkTarget) {
      this.artistLinkTargets.forEach((item) => {
        item.href = track.band_path
      })
    }
    this.composerLinkTargets.forEach((item) => {
      item.href = track.composer_url
    })
    this.performerLinkTargets.forEach((item) => {
      item.href = track.performer_url
    })
    this.favouriteLinkTargets.forEach((item) => {
      item.href = track.favourite_url
    })
    if (this.playableList?.current?.name) {
      // this is a playlist, so update the playlist data too
      if (this.hasPlaylistNameTarget) {
        this.playlistNameTargets.forEach((item) => {
          item.innerText = this.playableList.current.name
        })
      }
      if (this.hasPlaylistIndexTarget) {
        this.playlistIndexTargets.forEach((item) => {
          item.innerText = this.playableList.current._index + 1
        })
      }
      if (this.hasPlaylistLengthTarget) {
        this.playlistLengthTargets.forEach((item) => {
          item.innerText = this.playableList.current.tracks.length
        })
      }
      if (this.hasPlaylistLinkTarget) {
        this.playlistLinkTargets.forEach((item) => {
          item.href = this.playableList.current.path
        })
      }
    } else {
      // no playlist, empty playlist information
      this.playlistNameTargets.forEach((item) => {
        item.innerText = ""
      })
      this.playlistIndexTargets.forEach((item) => {
        item.innerText = ""
      })
      this.playlistLengthTargets.forEach((item) => {
        item.innerText = ""
      })
    }
  }

  togglePlay() {
    if (sharedState.isPlaying) {
      this.pause()
    } else {
      this.resume()
    }
  }

  setPoster(track) {
    this.playerTargets.forEach(target => { target.setAttribute("poster", track.picture_url_xlarge) })
    let backgroundImage = 'none'
    if (track.type == 'audio') {
      // Safari hides the poster when a background-color is set on the <video>, that's why we set it manually for audio tracks
      backgroundImage = `url('${track.picture_url_xlarge}')`
    }
    this.playerTargets.forEach(target => { target.style.backgroundImage = backgroundImage })
  }

  showControls(event) {
    if (!this.hasControlsTarget) return

    if (event) {
      // this was a real mouseenter, we wait for the mouseleave to hide the controls
      this.mouseOnPlayer = true
      // for the embed mode, we use the mouse events on the document, not on the <video>
      if (this.isEmbed() && event.target !== document) {
        return
      }
    } else {
      // this is not based on a mouse event: we hide the controls after 5 seconds,
      // if the mouse is not on the video
      setTimeout(() => {
        if (!this.mouseOnPlayer) {
          this.hideControls()
        }
      }, 5000)
    }
    this.controlsTarget.classList.add(this.showClass)
    this.controlsTarget.classList.remove(this.hiddenClass)
    if (this.hasVideoHeaderTarget) {
      this.videoHeaderTarget.classList.add(this.showClass)
      this.videoHeaderTarget.classList.remove(this.hiddenClass)
    }
  }

  hideControls(event) {
    if (event) {
      this.mouseOnPlayer = false
      // for the embed mode, we use the mouse events on the document, not on the <video>
      if (this.isEmbed()) {
        if (event.target !== document) return
        if (this.element.classList.contains("more-shown") ) {
          this.element.classList.remove("more-shown")
        }
      }
    }
    if (!sharedState.isPlaying) return
    this.controlsTarget.classList.add(this.hiddenClass)
    this.controlsTarget.classList.remove(this.showClass)
    if (this.hasVideoHeaderTarget) {
      this.videoHeaderTarget.classList.add(this.hiddenClass)
      this.videoHeaderTarget.classList.remove(this.showClass)
    }
  }

  broadcastState() {
    sharedState.players.forEach(player => {
      if (player !== this) {
        player.syncState()
      }
    })
  }

  syncState() {
    if (sharedState.isPlaying) {
      this.resume()
    } else {
      this.pause()
    }
  }

  formatSecondsToMinutes(seconds) {
    // Calculate the minutes and seconds
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = Math.floor(seconds % 60)

    // Pad with leading zeros if necessary
    const paddedMinutes = String(minutes).padStart(2, "0")
    const paddedSeconds = String(remainingSeconds).padStart(2, "0")

    // Combine and return the formatted string
    return `${paddedMinutes}:${paddedSeconds}`
  }

  /**
   * this method checks whether the currently played song/playlist is also the page we're currently on
   * if yes, restore the player to show the current progress
   */
  restoreCurrentTrackPlayer(self, sharedState) {
    const isPlaylistPage = document.querySelectorAll("#playlistPlay").length > 0
    const isTrackPage = document.querySelectorAll("#trackPlay").length > 0
    const placeholder = document.querySelector("#placeholder")

    if (!placeholder || !sharedState.playableList.current || (!isPlaylistPage && !isTrackPage)) {
      return
    }

    const trackConfig = JSON.parse(placeholder.getAttribute("data-playable-attributes-value") || "{}")
    const assetPath = trackConfig.playable?.path

    let currentPath
    if (isPlaylistPage && sharedState.playableList.current?.type === "Playlist") {
      currentPath = sharedState.playableList.current.path
    } else if (isTrackPage && sharedState.playableList.current?.type === "Track") {
      currentPath = sharedState.playableList.current.current?.path
    }

    if (assetPath === currentPath) {
      // wait for the track/playlist cover to load, otherwise we cannot calculate the player height
      const img = placeholder.querySelector("img")
      if (img.complete) {
        this.positionVideo()
      } else {
        img.onload = () => self.positionVideo()
      }
      this.showControls()
      if (isPlaylistPage) {
        this.dispatch("updateIndex", { detail: sharedState.playableList.current })
        let trackRow = document.querySelectorAll(".group\\/track")[sharedState.playableList.current._index]
        if (trackRow) { 
          trackRow.classList.add("selected")
          trackRow.querySelector('[data-playing-now-indicator-target]')?.classList.remove("hidden")
        }
      } 
    }
  }

  get currentTime() {
    return this.playerTarget.currentTime
  }

  get duration() {
    return this.playerTarget.duration
  }

  spaceDown(event) {
    if (!this.playableList.current || !this.shouldSpaceToggleThePlayer()) return    

    event.preventDefault()
    if (sharedState.isPlaying) {
      this.pause()
    } else {
      this.resume()
    }
  }

  shouldSpaceToggleThePlayer() {
    const activeElement = document.activeElement
    const interactiveElementFocused = 
      // Form inputs where space has meaning
      activeElement.tagName === "INPUT" || 
      activeElement.tagName === "TEXTAREA" || 
      activeElement.isContentEditable ||
      
      // Interactive controls
      activeElement.tagName === "BUTTON" ||
      activeElement.tagName === "SELECT" ||
      
      // Elements with role attributes for accessibility
      activeElement.getAttribute("role") === "button" ||
      activeElement.getAttribute("role") === "checkbox" ||
      activeElement.getAttribute("role") === "radio" ||
      activeElement.getAttribute("role") === "menuitem"

    // if the focus is on play button
    const isPlayButton = activeElement.dataset.action === "playable#play" 
    if (isPlayButton) {
      try {
        // check if the track playing is the one that is requested again
        const currentPath = this.playableList.current.current.path
        const requestedPath = JSON.parse(activeElement.dataset.playableAttributesValue).playable.path
        if (currentPath === requestedPath) return true
      } catch (e) {
        console.log("error reading current track path")
      }
    }

    return !interactiveElementFocused
  }

  playingIndicatorSet(event) {
    document.querySelectorAll("[data-controller='playable']").forEach((item) => {
      item.classList.remove("selected")
    })
    event.detail.closest("[data-controller='playable']").classList.add("selected")
  }

  loadTrackInfo(event) {
    if (event.detail?.type === "Playlist") {
      if (event.detail.picture_url_xlarge) {
        let backgroundImage = `url("${event.detail.picture_url_xlarge}")`
        this.playerTargets.forEach(target => { target.style.backgroundImage = backgroundImage })
        if (this.hasTrackCoverTarget) {
          this.trackCoverTargets.forEach((item) => {
            item.src = event.detail.picture_url_xlarge
          })
        }
        this.showControls()
      }
    } else if (event.detail.current.type === "video") {
      this.setPoster(event.detail.current)
      this.showControls()
    } else {
      this.updateInfo(event.detail.current)
    }
  }

  isEmbed() {
    return this.element.classList.contains("group/embed-wrapper")
  }
}
